'use client';

import Image from 'next/image';
import { Modal, Stack, Text, Title } from '@mantine/core';
import { IconX } from '@tabler/icons-react';

import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { LogoMark } from '@/components/LogoMark';
import { useAuthGateStore } from '@/stores/authGateStore';
import { useAuthStore, type PendingIntent } from '@/stores/authStore';
import { useAppModeStore } from '@/stores/appModeStore';

function intentCopy(intent: PendingIntent | null): string {
  switch (intent?.type) {
    case 'chat_submit':
      return 'Sign in to send your message.';
    case 'card_prompt':
      return 'Sign in to get started.';
    case 'voice':
      return 'Sign in to start a call.';
    default:
      return 'Sign in to continue.';
  }
}

export function AuthGateModal() {
  const isOpen = useAuthGateStore((s) => s.isOpen);
  const close = useAuthGateStore((s) => s.close);
  const pendingIntent = useAuthStore((s) => s.pendingIntent);
  const isAppMode = useAppModeStore((s) => s.isAppMode);
  const appConfig = useAppModeStore((s) => s.appConfig);

  const displayName = (isAppMode && appConfig?.display_name) || 'ReadyLoop';
  const logo = isAppMode && appConfig?.app_icon_url ? (
    <Image src={appConfig.app_icon_url} alt="" width={40} height={40} unoptimized />
  ) : (
    <LogoMark size={40} />
  );

  return (
    <Modal
      opened={isOpen}
      onClose={close}
      centered
      withCloseButton={false}
      size={340}
      title={null}
      padding={0}
    >
      <div style={{ position: 'relative', padding: '28px 28px 24px' }}>
        <button
          onClick={close}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            padding: 4,
            background: 'transparent',
            border: 'none',
            borderRadius: 4,
            color: 'var(--rl-fg-subtle)',
            cursor: 'pointer',
            display: 'flex',
          }}
        >
          <IconX size={14} />
        </button>
        <Stack align="center" gap={0}>
          {logo}
          <Title
            order={4}
            mt={14}
            style={{
              color: 'var(--rl-fg)',
              fontWeight: 600,
              fontFamily: "'IBM Plex Sans', sans-serif",
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              textAlign: 'center',
            }}
          >
            {displayName}
          </Title>
          <Text c="var(--rl-fg-muted)" size="sm" ta="center" mt={6}>
            {intentCopy(pendingIntent)}
          </Text>
          <div style={{ marginTop: 20 }}>
            <GoogleSignInButton />
          </div>
        </Stack>
      </div>
    </Modal>
  );
}
